// src/pages/admin/AdminUsersSection.jsx — User directory, RBAC, branches, Firebase Auth sync
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import bcrypt from "bcryptjs";
import toast from "react-hot-toast";
import { db } from "../../firebase";
import { downloadCsv } from "../../utils/csvExport";
import {
  adminGetAuthUser,
  adminRefreshUserClaims,
  adminDisableAuthUser,
  adminGeneratePasswordResetLink,
  adminEnsureAuthUser,
  adminQueryAuditLogs,
} from "../../services/adminApi";
import {
  PERMISSION_IDS,
  PERMISSION_LABELS,
  defaultPermissionsForRole,
  getEffectivePermissions,
  isAdminRole,
} from "../../config/permissions";
import { APP_BRANCHES, sanitizeAllowedBranchesArray } from "../../config/appBranches";

/** New users get a fixed legacy `role` field for Auth/Firestore rules; access is driven by `permissions`. */
const DEFAULT_NEW_USER_ROLE = "staff";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function randomPassword() {
  const a = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) crypto.getRandomValues(a);
  else for (let i = 0; i < a.length; i += 1) a[i] = Math.floor(Math.random() * 256);
  return btoa(String.fromCharCode(...a)).replace(/[^a-zA-Z0-9]/g, "").slice(0, 20);
}

function groupPermissionsBySection() {
  const groups = {};
  for (const id of PERMISSION_IDS) {
    const g = PERMISSION_LABELS[id]?.group || "Other";
    if (!groups[g]) groups[g] = [];
    groups[g].push(id);
  }
  return groups;
}

export function PermissionMatrix({ value, onChange, filter }) {
  const groups = useMemo(() => groupPermissionsBySection(), []);
  const fq = (filter || "").trim().toLowerCase();
  const set = new Set(value);
  const toggle = (id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  };
  const allIds = PERMISSION_IDS;
  const allSelected = allIds.length > 0 && allIds.every((id) => set.has(id));
  const toggleAll = () => {
    if (allSelected) onChange([]);
    else onChange([...allIds]);
  };

  const idMatches = (id) => {
    if (!fq) return true;
    const lab = (PERMISSION_LABELS[id]?.label || id).toLowerCase();
    const hint = (PERMISSION_LABELS[id]?.hint || "").toLowerCase();
    return lab.includes(fq) || hint.includes(fq) || id.includes(fq);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/50">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
        <span className="text-sm font-semibold text-slate-800">
          Access permissions <span className="font-mono text-xs text-indigo-600">({set.size}/{allIds.length})</span>
        </span>
        <button
          type="button"
          onClick={toggleAll}
          className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
        >
          {allSelected ? "Clear all" : "Select all"}
        </button>
      </div>
      <div className="grid max-h-[min(70vh,520px)] gap-4 overflow-y-auto p-4 sm:grid-cols-2 xl:grid-cols-3">
        {Object.entries(groups).map(([section, ids]) => {
          const visible = ids.filter(idMatches);
          if (!visible.length) return null;
          return (
            <div key={section} className="rounded-lg border border-slate-100 bg-white p-3">
              <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">{section}</p>
              <ul className="space-y-2">
                {visible.map((id) => (
                  <li key={id}>
                    <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-800">
                      <input
                        type="checkbox"
                        className="mt-0.5 rounded border-slate-300 text-indigo-600"
                        checked={set.has(id)}
                        onChange={() => toggle(id)}
                      />
                      <span className="flex flex-col gap-0.5">
                        <span>{PERMISSION_LABELS[id]?.label || id}</span>
                        {PERMISSION_LABELS[id]?.hint && (
                          <span className="text-[11px] font-normal leading-snug text-slate-500">
                            {PERMISSION_LABELS[id].hint}
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const PAYROLL_MENU_SCREENS = [
  { menu: "Monitor", route: "/payroll", purpose: "Monthly attendance & punches (per branch)" },
  { menu: "Monthly report", route: "/payroll/monthly-report", purpose: "All staff absent days (no punch) for the month" },
  { menu: "Daily hours", route: "/payroll/daily-hours", purpose: "Hours / OT from punches" },
  { menu: "Holidays", route: "/payroll/holidays", purpose: "Company holidays & employee cash leave" },
  { menu: "Employees", route: "/payroll/employees", purpose: "Roster master" },
];

function BranchAccessFields({ limited, onLimitedChange, selectedValues, onToggleBranch }) {
  return (
    <div className="space-y-4">
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-4 py-3">
        <p className="text-sm font-semibold text-slate-800">Branch access (orders, reports &amp; payroll)</p>
        <p className="mt-1 text-xs text-slate-500">
          Restrict which shop branches appear in Pending Orders, Order Reports, and all Payroll screens below. Empty =
          all branches. Admins always have all branches.
        </p>
      </div>
      <div className="p-4">
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-800">
          <input
            type="checkbox"
            className="rounded border-slate-300 text-indigo-600"
            checked={limited}
            onChange={(e) => onLimitedChange(e.target.checked)}
          />
          Limit to selected branches only
        </label>
        {limited ? (
          <ul className="mt-3 grid gap-2 sm:grid-cols-3">
            {APP_BRANCHES.map((b) => (
              <li key={b.value}>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-800">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 text-indigo-600"
                    checked={selectedValues.includes(b.value)}
                    onChange={() => onToggleBranch(b.value)}
                  />
                  <span>
                    {b.label} <span className="text-slate-400">({b.value})</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>

    <div className="rounded-xl border border-indigo-100 bg-indigo-50/40">
      <div className="border-b border-indigo-100/80 px-4 py-3">
        <p className="text-sm font-semibold text-indigo-950">Payroll menu (requires Payroll &amp; HR permission)</p>
        <p className="mt-1 text-xs text-indigo-900/80">
          Grant the <strong>Payroll &amp; HR</strong> checkbox above. Branch limit filters which shops appear in Monitor
          and Monthly report.
        </p>
      </div>
      <div className="overflow-x-auto p-3">
        <table className="min-w-full text-left text-xs text-indigo-950">
          <thead>
            <tr className="text-[10px] font-bold uppercase text-indigo-800/90">
              <th className="px-2 py-1.5">Menu</th>
              <th className="px-2 py-1.5">Route</th>
              <th className="px-2 py-1.5">Purpose</th>
            </tr>
          </thead>
          <tbody>
            {PAYROLL_MENU_SCREENS.map((row) => (
              <tr key={row.route} className="border-t border-indigo-100/60">
                <td className="px-2 py-1.5 font-semibold">{row.menu}</td>
                <td className="px-2 py-1.5 font-mono text-[11px]">{row.route}</td>
                <td className="px-2 py-1.5 text-indigo-900/90">{row.purpose}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    </div>
  );
}

function branchDirectoryLabel(u) {
  if (isAdminRole(u)) {
    return <span className="text-[11px] text-slate-500">All branches</span>;
  }
  const ab = u.allowedBranches;
  if (!Array.isArray(ab) || ab.length === 0) {
    return <span className="text-[11px] text-emerald-800">All</span>;
  }
  return <span className="max-w-[120px] truncate text-[11px] text-slate-700">{ab.join(", ")}</span>;
}

function DeleteConfirmModal({ open, targetEmail, value, onChange, onClose, onConfirm, busy }) {
  if (!open) return null;
  const ok = value.trim().toLowerCase() === String(targetEmail || "").trim().toLowerCase();
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-user-title"
    >
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
        <h2 id="delete-user-title" className="text-lg font-semibold text-slate-900">
          Delete user
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          Removes the Firestore user document. Type the user&apos;s email to confirm. Firebase Auth is not deleted
          automatically.
        </p>
        <label className="mt-4 block text-xs font-medium text-slate-600">Email</label>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          placeholder="user@company.com"
        />
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!ok || busy}
            onClick={onConfirm}
            className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminUsersSection() {
  const auth = getAuth();
  const searchRef = useRef(null);
  const usernameRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [inviteMode, setInviteMode] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [drawerUser, setDrawerUser] = useState(null);
  const [drawerAuth, setDrawerAuth] = useState(null);
  const [drawerAudit, setDrawerAudit] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [syncMap, setSyncMap] = useState({});
  const [formPermissions, setFormPermissions] = useState(() => defaultPermissionsForRole("staff"));
  const [branchAccessLimited, setBranchAccessLimited] = useState(false);
  const [formAllowedBranches, setFormAllowedBranches] = useState(() => APP_BRANCHES.map((b) => b.value));
  const [permSearch, setPermSearch] = useState("");
  const [showIds, setShowIds] = useState(false);
  const [cloneFromId, setCloneFromId] = useState("");

  const fetchUsers = useCallback(async () => {
    try {
      setListLoading(true);
      let snapshot;
      try {
        snapshot = await getDocs(query(collection(db, "users"), orderBy("createdAt", "desc")));
      } catch {
        snapshot = await getDocs(collection(db, "users"));
      }
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const ta = a.createdAt?.seconds ?? new Date(a.createdAt || 0).getTime() / 1000;
        const tb = b.createdAt?.seconds ?? new Date(b.createdAt || 0).getTime() / 1000;
        return tb - ta;
      });
      setUsers(list);
    } catch (err) {
      console.error("fetchUsers error:", err);
      toast.error("Failed to load users");
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      const editable =
        tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.target?.isContentEditable;
      if (e.key === "/" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (!editable || e.target?.type === "search") {
          e.preventDefault();
          searchRef.current?.focus();
        }
      }
      if (e.altKey && (e.key === "n" || e.key === "N")) {
        e.preventDefault();
        usernameRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const myDocId = useMemo(() => {
    const em = auth.currentUser?.email?.toLowerCase()?.trim();
    if (!em) return null;
    const row = users.find((u) => String(u.email || "").toLowerCase().trim() === em);
    return row?.id ?? null;
  }, [users, auth.currentUser?.email]);

  const totalUsers = users.length;

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        String(u.username || "")
          .toLowerCase()
          .includes(q) ||
        String(u.email || "")
          .toLowerCase()
          .includes(q)
    );
  }, [users, search]);
  const allFilteredSelected = filteredUsers.length > 0 && filteredUsers.every((u) => selectedIds.includes(u.id));

  const resetForm = () => {
    setUsername("");
    setEmail("");
    setPassword("");
    setEditingId(null);
    setInviteMode(false);
    setFormPermissions(defaultPermissionsForRole("staff"));
    setBranchAccessLimited(false);
    setFormAllowedBranches(APP_BRANCHES.map((b) => b.value));
    setCloneFromId("");
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setUsername(u.username || "");
    setEmail(u.email || "");
    setPassword("");
    setInviteMode(false);
    setFormPermissions(
      Array.isArray(u.permissions)
        ? [...u.permissions]
        : defaultPermissionsForRole("staff")
    );
    const rawAb = u.allowedBranches;
    const limited = Array.isArray(rawAb) && rawAb.length > 0;
    setBranchAccessLimited(limited);
    setFormAllowedBranches(
      limited ? sanitizeAllowedBranchesArray(rawAb) || APP_BRANCHES.map((b) => b.value) : APP_BRANCHES.map((b) => b.value)
    );
    setCloneFromId("");
    toast.success("Editing user — update fields and save");
  };

  const toggleFormBranch = (value) => {
    setFormAllowedBranches((prev) => {
      const s = new Set(prev);
      if (s.has(value)) s.delete(value);
      else s.add(value);
      return [...s];
    });
  };

  const applyCloneFrom = () => {
    if (!cloneFromId) {
      toast.error("Pick a user to copy from");
      return;
    }
    const src = users.find((u) => u.id === cloneFromId);
    if (!src) return;
    setFormPermissions(
      Array.isArray(src.permissions)
        ? [...src.permissions]
        : defaultPermissionsForRole("staff")
    );
    const rawAb = src.allowedBranches;
    const limited = Array.isArray(rawAb) && rawAb.length > 0;
    setBranchAccessLimited(limited);
    setFormAllowedBranches(
      limited ? sanitizeAllowedBranchesArray(rawAb) || APP_BRANCHES.map((b) => b.value) : APP_BRANCHES.map((b) => b.value)
    );
    toast.success(`Copied access from ${src.username || src.email}`);
  };

  const hashPassword = (plain) =>
    new Promise((resolve, reject) => {
      bcrypt.genSalt(10, (err, salt) => {
        if (err) return reject(err);
        bcrypt.hash(plain, salt, (err2, hash) => {
          if (err2) return reject(err2);
          resolve(hash);
        });
      });
    });

  const refreshClaimsForUser = async (u) => {
    const uid = u.authUid;
    if (!uid) {
      toast.error("No auth UID — run “Sync to Auth” first.");
      return;
    }
    try {
      await adminRefreshUserClaims({
        uid,
        role: String(u.role || "staff"),
        username: String(u.username || ""),
        mustChangePassword: !!u.mustChangePassword,
        permissions: getEffectivePermissions(u),
      });
      toast.success("Custom claims refreshed");
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to refresh claims");
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!username.trim()) {
      toast.error("Enter username");
      return;
    }
    if (!email.trim()) {
      toast.error("Enter email");
      return;
    }
    const normalizedEmail = email.trim().toLowerCase();
    if (!EMAIL_RE.test(normalizedEmail)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (!editingId && !inviteMode && !password) {
      toast.error("Enter password for new user, or enable Invite mode");
      return;
    }
    let allowedBranchesPayload = [];
    if (branchAccessLimited) {
      const cleaned = sanitizeAllowedBranchesArray(formAllowedBranches);
      if (!cleaned?.length) {
        toast.error("Select at least one branch, or turn off branch limit");
        return;
      }
      allowedBranchesPayload = cleaned;
    }

    setLoading(true);
    try {
      if (!editingId) {
        const dup = users.some((u) => String(u.email || "").toLowerCase().trim() === normalizedEmail);
        if (dup) {
          toast.error("That email is already registered");
          setLoading(false);
          return;
        }
        const plain = inviteMode ? randomPassword() : password;
        const hashedPassword = await hashPassword(plain);
        const uname = username.trim();
        const ref = await addDoc(collection(db, "users"), {
          username: uname,
          usernameLower: uname.toLowerCase(),
          email: normalizedEmail,
          password: hashedPassword,
          passwordHash: hashedPassword,
          role: DEFAULT_NEW_USER_ROLE,
          permissions: formPermissions,
          allowedBranches: allowedBranchesPayload,
          createdAt: serverTimestamp(),
          ...(inviteMode ? { mustChangePassword: true, invitedAt: serverTimestamp() } : {}),
        });
        toast.success("User added");

        try {
          const res = await adminEnsureAuthUser({
            firestoreDocId: ref.id,
            mustChangePassword: !!inviteMode,
            initialPassword: plain,
          });
          if (inviteMode && res.data?.passwordResetLink) {
            setInviteLink(res.data.passwordResetLink);
            toast.success("Firebase Auth linked — share reset link if the user sets their own password");
          } else {
            toast.success("Firebase Authentication linked");
          }
        } catch (err) {
          console.error(err);
          toast.error(err?.message || "Saved in Firestore but Auth link failed. Use Sync to Auth.");
        }

        resetForm();
        await fetchUsers();
        return;
      }

      const emailLower = normalizedEmail;
      const emailTaken = users.some(
        (u) => u.id !== editingId && String(u.email || "").toLowerCase().trim() === emailLower
      );
      if (emailTaken) {
        toast.error("That email is already used by another user");
        setLoading(false);
        return;
      }
      const uname = username.trim();
      const payload = {
        username: uname,
        usernameLower: uname.toLowerCase(),
        email: emailLower,
        permissions: formPermissions,
        allowedBranches: allowedBranchesPayload,
        updatedAt: serverTimestamp(),
      };
      if (password.trim()) {
        const hp = await hashPassword(password);
        payload.password = hp;
        payload.passwordHash = hp;
      }
      await updateDoc(doc(db, "users", editingId), payload);
      const prev = users.find((x) => x.id === editingId);
      const updated = { ...prev, ...payload };

      if (password.trim()) {
        try {
          await adminEnsureAuthUser({
            firestoreDocId: editingId,
            initialPassword: password.trim(),
            mustChangePassword: false,
          });
        } catch (err) {
          console.warn(err);
          toast.error(err?.message || "Saved in Firestore but Auth password sync failed");
        }
      } else if (updated.authUid) {
        try {
          await adminRefreshUserClaims({
            uid: updated.authUid,
            role: String(updated.role || "staff"),
            username: String(updated.username || ""),
            mustChangePassword: !!updated.mustChangePassword,
            permissions: getEffectivePermissions({
              role: updated.role,
              permissions: formPermissions,
            }),
          });
        } catch (err) {
          console.warn("Claims refresh after edit:", err);
        }
      }
      toast.success("User updated");
      resetForm();
      await fetchUsers();
    } catch (err) {
      console.error(err);
      toast.error(editingId ? "Failed to update user" : "Failed to add user");
    } finally {
      setLoading(false);
    }
  };

  const openDelete = (u) => {
    if (u.id === myDocId) {
      toast.error("You cannot delete your own account here");
      return;
    }
    setDeleteModal({ id: u.id, email: u.email || "" });
    setDeleteConfirmInput("");
  };

  const confirmDelete = async () => {
    if (!deleteModal?.id) return;
    setDeleteBusy(true);
    try {
      await deleteDoc(doc(db, "users", deleteModal.id));
      toast.success("User removed");
      if (editingId === deleteModal.id) resetForm();
      setDeleteModal(null);
      setSelectedIds((prev) => prev.filter((x) => x !== deleteModal.id));
      await fetchUsers();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete");
    } finally {
      setDeleteBusy(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const selectAllFiltered = () => {
    const filteredIdSet = new Set(filteredUsers.map((u) => u.id));
    const allFilteredSelected = filteredUsers.length > 0 && filteredUsers.every((u) => selectedIds.includes(u.id));
    if (allFilteredSelected) {
      // Unselect only visible rows, keep external selections untouched.
      setSelectedIds((prev) => prev.filter((id) => !filteredIdSet.has(id)));
      return;
    }
    // Add only missing visible rows.
    setSelectedIds((prev) => [...new Set([...prev, ...filteredUsers.map((u) => u.id)])]);
  };

  const exportUsersCsv = () => {
    const rows = selectedIds.length ? filteredUsers.filter((u) => selectedIds.includes(u.id)) : filteredUsers;
    if (!rows.length) {
      toast.error("Nothing to export");
      return;
    }
    downloadCsv(
      `users-export-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        { key: "username", header: "username" },
        { key: "email", header: "email" },
        { key: "perms", header: "permissions_count" },
        { key: "branches", header: "allowedBranches" },
        { key: "authUid", header: "authUid" },
        { key: "id", header: "firestoreId" },
      ],
      rows.map((u) => ({
        ...u,
        perms: getEffectivePermissions(u).length,
        branches: Array.isArray(u.allowedBranches) && u.allowedBranches.length ? u.allowedBranches.join("|") : "ALL",
      }))
    );
    toast.success("CSV downloaded");
  };

  const loadAuthSync = async (u) => {
    const em = String(u.email || "").trim().toLowerCase();
    if (!em) {
      toast.error("User has no email");
      return;
    }
    setSyncMap((m) => ({ ...m, [u.id]: "loading" }));
    try {
      const res = await adminGetAuthUser({ email: em });
      setSyncMap((m) => ({ ...m, [u.id]: res.data }));
    } catch (err) {
      console.error(err);
      setSyncMap((m) => ({ ...m, [u.id]: { error: err?.message || "error" } }));
      toast.error("Could not load Auth user");
    }
  };

  const syncToAuth = async (u) => {
    try {
      const res = await adminEnsureAuthUser({
        firestoreDocId: u.id,
        mustChangePassword: !!u.mustChangePassword,
      });
      toast.success("Auth user synced");
      if (res.data?.passwordResetLink) setInviteLink(res.data.passwordResetLink);
      const uid = res.data?.uid;
      await fetchUsers();
      await loadAuthSync({ ...u, authUid: uid || u.authUid });
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Sync failed");
    }
  };

  const sendResetLink = async (em) => {
    try {
      const res = await adminGeneratePasswordResetLink({ email: em.trim().toLowerCase() });
      const link = res.data?.link;
      if (link) {
        await navigator.clipboard.writeText(link);
        toast.success("Password reset link copied");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to generate link");
    }
  };

  const setAuthDisabled = async (u, disabled) => {
    const uid = u.authUid || syncMap[u.id]?.uid;
    if (!uid) {
      toast.error("Load Auth sync first");
      return;
    }
    try {
      await adminDisableAuthUser({ uid, disabled });
      toast.success(disabled ? "Auth user disabled" : "Auth user enabled");
      await loadAuthSync({ ...u, authUid: uid });
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed");
    }
  };

  const loadDrawer = useCallback(async (u) => {
    setDrawerUser(u);
    setDrawerAuth(null);
    setDrawerAudit([]);
    setDrawerLoading(true);
    const em = String(u.email || "").trim().toLowerCase();
    try {
      if (em) {
        const [authRes, auditRes] = await Promise.all([
          adminGetAuthUser({ email: em }),
          adminQueryAuditLogs({ byEmail: em, days: 30, limit: 50 }),
        ]);
        setDrawerAuth(authRes.data);
        setDrawerAudit(auditRes.data?.rows || []);
      }
    } catch (err) {
      console.error(err);
      toast.error("Could not load user details");
    } finally {
      setDrawerLoading(false);
    }
  }, []);

  const formatCreated = (u) => {
    const c = u.createdAt;
    if (!c) return "—";
    if (typeof c?.seconds === "number") return new Date(c.seconds * 1000).toLocaleString("en-IN");
    if (c.toDate) return c.toDate().toLocaleString("en-IN");
    try {
      return new Date(c).toLocaleString("en-IN");
    } catch {
      return "—";
    }
  };

  const syncBadge = (u) => {
    const data = syncMap[u.id];
    if (data === "loading") return <span className="text-xs text-slate-500">…</span>;
    if (data?.error) return <span className="text-xs text-rose-600">Error</span>;
    if (!data || data.found === undefined) return <span className="text-xs text-slate-400">—</span>;
    if (!data.found) return <span className="text-xs text-amber-700">No Auth</span>;
    const claimsRole = String(data.claimsRole || "").trim().toLowerCase();
    const fsRole = String(u.role || DEFAULT_NEW_USER_ROLE).trim().toLowerCase();
    const drift = claimsRole ? claimsRole !== fsRole : false;
    return (
      <span
        className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
          drift ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-900"
        }`}
      >
        {drift ? "Claims ≠ FS" : claimsRole ? "OK" : "Auth linked"}
      </span>
    );
  };

  const fillDemo = () => {
    setUsername("john.doe");
    setEmail(`john${Math.floor(Math.random() * 1000)}@example.com`);
    setPassword("Password@123");
  };

  return (
    <div className="space-y-6">
      <DeleteConfirmModal
        open={!!deleteModal}
        targetEmail={deleteModal?.email}
        value={deleteConfirmInput}
        onChange={setDeleteConfirmInput}
        onClose={() => setDeleteModal(null)}
        onConfirm={confirmDelete}
        busy={deleteBusy}
      />

      {inviteLink && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/50 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-slate-900">Password reset link</h2>
            <p className="mt-2 text-sm text-slate-600">Share securely; one-time use.</p>
            <textarea readOnly className="mt-3 w-full rounded-xl border bg-slate-50 p-3 font-mono text-xs" rows={3} value={inviteLink} />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={async () => {
                  await navigator.clipboard.writeText(inviteLink);
                  toast.success("Copied");
                }}
                className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white"
              >
                Copy
              </button>
              <button type="button" onClick={() => setInviteLink(null)} className="rounded-xl border px-4 py-2 text-sm">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {drawerUser && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <button type="button" className="absolute inset-0 bg-slate-900/40" aria-label="Close" onClick={() => setDrawerUser(null)} />
          <aside className="relative z-10 flex h-full w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-2xl">
            <div className="border-b p-5">
              <div className="flex justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500">User</p>
                  <h2 className="text-lg font-bold text-slate-900">{drawerUser.username || "—"}</h2>
                  <p className="text-sm text-slate-600">{drawerUser.email}</p>
                </div>
                <button type="button" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" onClick={() => setDrawerUser(null)}>
                  ✕
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-5 text-sm">
              {drawerLoading ? (
                <p className="text-slate-500">Loading…</p>
              ) : (
                <>
                  <section className="rounded-xl border border-slate-100 bg-slate-50/80 p-4">
                    <h3 className="font-semibold text-slate-900">Firebase Auth</h3>
                    {drawerAuth?.found === false && (
                      <p className="mt-2 text-amber-800">No Auth user — use Sync to Auth on the row.</p>
                    )}
                    {drawerAuth?.found && (
                      <ul className="mt-2 space-y-1 text-slate-700">
                        <li>
                          <span className="text-slate-500">UID:</span> {drawerAuth.uid}
                        </li>
                        <li>
                          <span className="text-slate-500">Claims branches:</span>{" "}
                          {(drawerAuth.claimsAllowedBranches || []).join(", ") || "—"}
                        </li>
                      </ul>
                    )}
                  </section>
                  <section className="mt-4">
                    <h3 className="font-semibold text-slate-900">Recent audit (30d)</h3>
                    <ul className="mt-2 max-h-48 space-y-2 overflow-y-auto text-xs">
                      {drawerAudit.length === 0 ? (
                        <li className="text-slate-500">No entries</li>
                      ) : (
                        drawerAudit.map((a) => (
                          <li key={a.id} className="rounded border border-slate-100 bg-white px-2 py-1">
                            {a.action} — {a.entity}
                          </li>
                        ))
                      )}
                    </ul>
                  </section>
                </>
              )}
            </div>
          </aside>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm sm:inline-block">
        <p className="text-2xl font-bold tabular-nums text-slate-900">{totalUsers}</p>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Users</p>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{editingId ? "Edit user" : "Add user"}</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Firestore is source of truth; custom claims sync when <code className="rounded bg-slate-100 px-1">authUid</code> exists.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!editingId && (
              <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={inviteMode}
                  onChange={(e) => setInviteMode(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600"
                />
                Invite (reset link)
              </label>
            )}
            {editingId && (
              <button type="button" onClick={resetForm} className="rounded-lg border px-3 py-1.5 text-sm">
                Cancel edit
              </button>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Username</label>
              <input
                ref={usernameRef}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder="j.smith"
                autoComplete="username"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Email</label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                Password {editingId && <span className="text-slate-400">(optional)</span>}
              </label>
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                disabled={!editingId && inviteMode}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm disabled:opacity-50"
                autoComplete="new-password"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-indigo-100 bg-indigo-50/50 px-4 py-3">
            <span className="text-xs font-semibold text-indigo-900">Quick presets</span>
            <button
              type="button"
              className="rounded-lg bg-white px-2 py-1 text-xs font-medium text-slate-800 shadow-sm"
              onClick={() => setFormPermissions([...PERMISSION_IDS])}
            >
              Full ({PERMISSION_IDS.length})
            </button>
            <button
              type="button"
              className="rounded-lg bg-white px-2 py-1 text-xs font-medium text-slate-800 shadow-sm"
              onClick={() => setFormPermissions(defaultPermissionsForRole("staff"))}
            >
              Trading / ops preset
            </button>
            <button
              type="button"
              className="rounded-lg bg-white px-2 py-1 text-xs font-medium text-slate-800 shadow-sm"
              onClick={() => setFormPermissions(defaultPermissionsForRole("accounts"))}
            >
              Finance preset
            </button>
            <button
              type="button"
              className="rounded-lg bg-white px-2 py-1 text-xs font-medium text-slate-800 shadow-sm"
              onClick={() =>
                setFormPermissions([
                  "dashboard",
                  "change_password",
                  "payroll",
                ])
              }
            >
              Payroll / HR preset
            </button>
            <button
              type="button"
              className="rounded-lg bg-white px-2 py-1 text-xs font-medium text-slate-800 shadow-sm"
              onClick={() => setFormPermissions(defaultPermissionsForRole("viewer"))}
            >
              Read-only preset
            </button>
            <button
              type="button"
              className="rounded-lg bg-white px-2 py-1 text-xs font-medium text-rose-700 shadow-sm"
              onClick={() => setFormPermissions([])}
            >
              Clear permissions
            </button>
          </div>

          <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="min-w-[200px] flex-1">
              <label className="mb-1 block text-xs font-medium text-slate-600">Clone access from existing user</label>
              <select
                value={cloneFromId}
                onChange={(e) => setCloneFromId(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
              >
                <option value="">— Select user —</option>
                {users
                  .filter((u) => u.id !== editingId)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.username || u.email}
                    </option>
                  ))}
              </select>
            </div>
            <button type="button" onClick={applyCloneFrom} className="rounded-xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white">
              Apply clone
            </button>
          </div>

          <div>
            <label className="mb-2 block text-xs font-medium text-slate-600">Filter permissions</label>
            <input
              value={permSearch}
              onChange={(e) => setPermSearch(e.target.value)}
              placeholder="Search label, route, or id…"
              className="mb-3 w-full max-w-md rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />
            <PermissionMatrix value={formPermissions} onChange={setFormPermissions} filter={permSearch} />
          </div>

          <BranchAccessFields
            limited={branchAccessLimited}
            onLimitedChange={(v) => {
              setBranchAccessLimited(v);
              if (v && formAllowedBranches.length === 0) setFormAllowedBranches(APP_BRANCHES.map((b) => b.value));
            }}
            selectedValues={formAllowedBranches}
            onToggleBranch={toggleFormBranch}
          />

          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow disabled:opacity-50"
            >
              {loading ? "Saving…" : editingId ? "Save changes" : "Add user"}
            </button>
            <button type="button" onClick={fillDemo} className="text-sm text-slate-500 underline-offset-2 hover:text-indigo-600 hover:underline">
              Fill demo fields
            </button>
          </div>
        </form>
      </section>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Directory</h2>
            <p className="text-sm text-slate-500">
              <kbd className="rounded bg-slate-100 px-1">/</kbd> search · <kbd className="rounded bg-slate-100 px-1">Alt+N</kbd>{" "}
              add user
            </p>
          </div>
          <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
            <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={showIds} onChange={(e) => setShowIds(e.target.checked)} className="rounded" />
              Show IDs
            </label>
            <input
              ref={searchRef}
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="max-w-xs flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={() => fetchUsers()}
              disabled={listLoading}
              className="rounded-xl border px-3 py-2 text-sm font-medium"
            >
              Refresh
            </button>
          </div>
        </div>

        {selectedIds.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-indigo-100 bg-indigo-50/90 px-5 py-2 text-sm">
            <span className="font-medium text-indigo-950">{selectedIds.length} selected</span>
            <button type="button" onClick={() => setSelectedIds([])} className="text-xs font-semibold text-indigo-800">
              Clear
            </button>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 border-b px-5 py-3">
          <button type="button" onClick={exportUsersCsv} className="rounded-lg border bg-white px-3 py-1.5 text-xs font-semibold">
            Export CSV
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-left text-sm">
            <thead className="sticky top-0 bg-slate-50 text-[11px] font-semibold uppercase text-slate-500">
              <tr>
                <th className="px-2 py-3">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={selectAllFiltered}
                    aria-label="Select all"
                  />
                </th>
                <th className="px-3 py-3">User</th>
                <th className="px-3 py-3">Email</th>
                <th className="px-3 py-3">Perms</th>
                <th className="px-3 py-3">Branches</th>
                <th className="px-3 py-3">Auth</th>
                <th className="px-3 py-3">Created</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {listLoading ? (
                <tr>
                  <td colSpan={8} className="px-5 py-12 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-5 py-12 text-center text-slate-600">
                    No users found.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => (
                  <tr key={u.id} className={selectedIds.includes(u.id) ? "bg-indigo-50/50" : "hover:bg-slate-50/80"}>
                    <td className="px-2 py-3">
                      <input type="checkbox" checked={selectedIds.includes(u.id)} onChange={() => toggleSelect(u.id)} />
                    </td>
                    <td className="px-3 py-3">
                      <div className="font-medium text-slate-900">{u.username || "—"}</div>
                      {showIds && <div className="font-mono text-[10px] text-slate-400">{u.id}</div>}
                    </td>
                    <td className="max-w-[200px] truncate px-3 py-3 text-slate-600">{u.email || "—"}</td>
                    <td className="px-3 py-3 text-xs tabular-nums text-slate-700">
                      {getEffectivePermissions(u).length}/{PERMISSION_IDS.length}
                    </td>
                    <td className="px-3 py-3 align-top">{branchDirectoryLabel(u)}</td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-1">
                        {syncBadge(u)}
                        <div className="flex flex-wrap gap-1">
                          <button type="button" onClick={() => loadAuthSync(u)} className="rounded border px-1 py-0.5 text-[10px]">
                            Load
                          </button>
                          <button type="button" onClick={() => syncToAuth(u)} className="rounded border border-indigo-200 bg-indigo-50 px-1 py-0.5 text-[10px] text-indigo-800">
                            Sync Auth
                          </button>
                          {(u.authUid || syncMap[u.id]?.uid) && (
                            <button
                              type="button"
                              onClick={() =>
                                refreshClaimsForUser({
                                  ...u,
                                  authUid: u.authUid || syncMap[u.id]?.uid,
                                })
                              }
                              className="rounded border px-1 py-0.5 text-[10px]"
                            >
                              Refresh claims
                            </button>
                          )}
                          {(u.authUid || syncMap[u.id]?.uid) && (
                            <>
                              <button type="button" onClick={() => sendResetLink(u.email)} className="rounded border px-1 py-0.5 text-[10px]">
                                Reset link
                              </button>
                              {syncMap[u.id]?.found && (u.authUid || syncMap[u.id]?.uid) && (
                                <button
                                  type="button"
                                  onClick={() => setAuthDisabled(u, !syncMap[u.id].disabled)}
                                  className="rounded border border-rose-100 px-1 py-0.5 text-[10px] text-rose-800"
                                >
                                  {syncMap[u.id].disabled ? "Enable" : "Disable"}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                        {showIds && (u.authUid || syncMap[u.id]?.uid) && (
                          <div className="font-mono text-[10px] text-slate-400">{u.authUid || syncMap[u.id]?.uid}</div>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-slate-500">{formatCreated(u)}</td>
                    <td className="whitespace-nowrap px-3 py-3 text-right">
                      <button type="button" onClick={() => loadDrawer(u)} className="mr-1 rounded border px-2 py-1 text-xs">
                        Details
                      </button>
                      <button type="button" onClick={() => startEdit(u)} className="mr-1 rounded border px-2 py-1 text-xs">
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => openDelete(u)}
                        disabled={u.id === myDocId}
                        className="rounded bg-rose-600 px-2 py-1 text-xs text-white disabled:opacity-40"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
